import { useState } from 'react';
import axios from 'axios';
import { QA_ENDPOINT, PRODUCTION_ENDPOINT, BASE_URL_AUTH } from '../constants/endpoints';
import { useStore } from '../store';
import { getToken } from '../Utils';
// custom hook for performing GET request
export const useAxios = (
  defaultResponse,
  successMessage = 'THIS OPERATION WAS SUCCESSFUL',
  showSuccessMessage = true,
  customErrorMessage = "VALUE REFERENCED, CAN'T BE DELETED",
) => {
  const [data, setData] = useState(defaultResponse);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const { handleAlert } = useStore((state) => ({
    handleAlert: state.handleAlert,
  }));

  const instanceAxios = axios.create({
    baseURL: PRODUCTION_ENDPOINT || QA_ENDPOINT,
  });

  instanceAxios.interceptors.request.use(
    (req) => {
      if (req.method === 'post' || req.method === 'put' || req.method === 'get') {
        req.headers.Authorization = getToken();
        req.headers['Content-Type'] = 'application/json';
      }
      return req;
    },
    (error) => {
      setLoading(false);
      return Promise.reject(error);
    },
  );

  instanceAxios.interceptors.response.use(
    (response) => {
      if (response.status >= 200 && response.status < 300) {
        if (showSuccessMessage) {
          handleAlert({
            open: true,
            message: successMessage,
          });
        }
      }
      return response;
    },
    (error) => {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest._retry) {
        if (error.response && error.response.data && error.response.data.errorCode) {
          switch (error.response.data.errorCode) {
            case 'auth05':
              originalRequest._retry = true;
              const refreshToken = localStorage.getItem('refreshToken');
              return axios
                .post(`${BASE_URL_AUTH}/refresh`, {
                  refreshToken,
                })
                .then((res) => {
                  if (res.status === 200) {
                    localStorage.setItem('token', res.data.idToken);
                    return instanceAxios(originalRequest);
                  }
                })
                .catch((err) => {
                  console.log(err);
                });
            default:
              break;
          }
        }
      }
    },
  );

  const resetResult = () => {
    setData(defaultResponse);
    setLoading(false);
    setError(null);
    setStatusCode(null);
  };

  const source = axios.CancelToken.source();
  const BASE_URL = PRODUCTION_ENDPOINT || QA_ENDPOINT;
  const fetchData = async function (endPoint = '', method = 'get', axiosOptions = {}) {
    const url = `${BASE_URL}${endPoint}`;
    const defaultOptions = {
      method,
      url,
      cancelToken: source.token,
      headers: {
        'Content-type': 'application/json; charset=UTF-8',
        Authorization: getToken(),
      },
      ...axiosOptions,
    };
    try {
      setLoading(true);
      const response = await axios({ ...defaultOptions });
      setData(response.data || response.content);
      setStatusCode(response.status);
      if (response.status >= 200 && response.status < 300 && method.toLowerCase() !== 'get') {
        if (showSuccessMessage) {
          handleAlert({
            open: true,
            message: successMessage,
          });
        }
        return response;
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('AxiosCancel: caught cancel');
      } else {
        setError(error);
        const dependencyMessage =
          'Constraint violation exception. Please validate ID and/or other UNIQUE fields are not already created in the DB';
        const isDependencyMessage = error?.response?.data?.errorMessage === dependencyMessage;

        const errorMessage = isDependencyMessage
          ? customErrorMessage
          : error?.response?.data?.errorMessage || error;
        handleAlert({
          open: true,
          message: `ERROR MAKING A CALL:  ${errorMessage}`,
          alertSeverity: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return [{ data, loading, error, source, statusCode, resetResult }, fetchData, instanceAxios];
};
